<template>
  <div class="quick-date-range-picker">
    <el-button-group class="quick-selection">
      <el-button 
        size="small"
        :type="activePreset === 'yesterday' ? 'primary' : ''"
        @click="selectPreset('yesterday')"
      >
        {{ $t('common.yesterday') }}
      </el-button>
      <el-button 
        size="small"
        :type="activePreset === 'today' ? 'primary' : ''"
        @click="selectPreset('today')"
      >
        {{ $t('common.today') }}
      </el-button>
      <el-button 
        size="small"
        :type="activePreset === 'thisWeek' ? 'primary' : ''"
        @click="selectPreset('thisWeek')"
      >
        {{ $t('common.thisWeek') }}
      </el-button>
      <el-button 
        size="small"
        :type="activePreset === 'lastWeek' ? 'primary' : ''"
        @click="selectPreset('lastWeek')"
      >
        {{ $t('common.lastWeek') }}
      </el-button>
      <el-button 
        size="small"
        :type="activePreset === 'thisMonth' ? 'primary' : ''"
        @click="selectPreset('thisMonth')"
      >
        {{ $t('common.thisMonth') || 'This Month' }}
      </el-button>
      <el-button 
        size="small"
        :type="activePreset === 'lastMonth' ? 'primary' : ''"
        @click="selectPreset('lastMonth')"
      >
        {{ $t('common.lastMonth') || 'Last Month' }}
      </el-button>
      <el-button 
        size="small"
        :type="activePreset === 'thisYear' ? 'primary' : ''"
        @click="selectPreset('thisYear')"
      >
        {{ $t('common.thisYear') || 'This Year' }}
      </el-button>
      <el-button 
        size="small"
        :type="activePreset === 'lastYear' ? 'primary' : ''"
        @click="selectPreset('lastYear')"
      >
        {{ $t('common.lastYear') || 'Last Year' }}
      </el-button>
    </el-button-group>
    
    <el-divider direction="vertical" />
    
    <el-date-picker
      v-model="internalDateRange"
      type="daterange"
      range-separator="~"
      :start-placeholder="$t('common.startDate')"
      :end-placeholder="$t('common.endDate')"
      value-format="YYYY-MM-DD"
      :clearable="true"
      format="YYYY-MM-DD"
      @change="handleDateChange"
    />
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import MTime from '@/utils/time-handler'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const activePreset = ref('')
const internalDateRange = ref(props.modelValue || [])

// Watch for external changes
watch(() => props.modelValue, (newVal) => {
  if (newVal && newVal.length !== internalDateRange.value.length) {
    internalDateRange.value = newVal
  }
}, { deep: true })

// Helper functions to get date ranges
const getYesterdayRange = () => {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  
  const year = yesterday.getFullYear()
  const month = String(yesterday.getMonth() + 1).padStart(2, '0')
  const day = String(yesterday.getDate()).padStart(2, '0')
  const yesterdayStr = `${year}-${month}-${day}`
  
  return [yesterdayStr, yesterdayStr]
}

const getTodayRange = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  const todayStr = `${year}-${month}-${day}`
  
  return [todayStr, todayStr]
}

const getThisWeekRange = () => {
  const today = new Date()
  const dayOfWeek = today.getDay()
  const monday = new Date(today)
  monday.setDate(today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1))
  
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  
  return [
    MTime.formatDate(monday),
    MTime.formatDate(sunday)
  ]
}

const getLastWeekRange = () => {
  const today = new Date()
  const dayOfWeek = today.getDay()
  const lastMonday = new Date(today)
  lastMonday.setDate(today.getDate() - dayOfWeek - 6)
  
  const lastSunday = new Date(lastMonday)
  lastSunday.setDate(lastMonday.getDate() + 6)
  
  return [
    MTime.formatDate(lastMonday),
    MTime.formatDate(lastSunday)
  ]
}

const getThisMonthRange = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = today.getMonth()
  
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  
  const startDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(firstDay.getDate()).padStart(2, '0')}`
  const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`
  
  return [startDate, endDate]
}

const getLastMonthRange = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = today.getMonth() - 1
  
  const firstDay = new Date(year, month, 1)
  const lastDay = new Date(year, month + 1, 0)
  
  const startDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(firstDay.getDate()).padStart(2, '0')}`
  const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`
  
  return [startDate, endDate]
}

const getThisYearRange = () => {
  const year = new Date().getFullYear()
  return [`${year}-01-01`, `${year}-12-31`]
}

const getLastYearRange = () => {
  const year = new Date().getFullYear() - 1
  return [`${year}-01-01`, `${year}-12-31`]
}

// Convert display dates to API format (UTC ISO 8601 timestamps)
const convertToAPIFormat = (dateRange) => {
  if (!dateRange || dateRange.length !== 2) {
    return { startDate: '', endDate: '' }
  }
  
  // Use new server-side date format
  return {
    startDate: MTime.formatQueryStartDate(dateRange[0]),
    endDate: MTime.formatQueryEndDate(dateRange[1])
  }
}

// Select preset range
const selectPreset = (preset) => {
  activePreset.value = preset
  
  let range = []
  switch (preset) {
    case 'yesterday':
      range = getYesterdayRange()
      break
    case 'today':
      range = getTodayRange()
      break
    case 'thisWeek':
      range = getThisWeekRange()
      break
    case 'lastWeek':
      range = getLastWeekRange()
      break
    case 'thisMonth':
      range = getThisMonthRange()
      break
    case 'lastMonth':
      range = getLastMonthRange()
      break
    case 'thisYear':
      range = getThisYearRange()
      break
    case 'lastYear':
      range = getLastYearRange()
      break
  }
  
  internalDateRange.value = range
  const apiDates = convertToAPIFormat(range)
  emit('update:modelValue', range)
  emit('change', apiDates)
}

// Handle manual date picker change
const handleDateChange = (value) => {
  activePreset.value = ''
  const apiDates = convertToAPIFormat(value)
  emit('update:modelValue', value)
  emit('change', apiDates)
}
</script>

<style scoped>
.quick-date-range-picker {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-family: "UKIJ Ekran", -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.quick-date-range-picker :deep(.el-button),
.quick-date-range-picker :deep(.el-input__inner),
.quick-date-range-picker :deep(.el-range-input),
.quick-date-range-picker :deep(.el-range-separator) {
  font-family: "UKIJ Ekran", -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.quick-selection {
  display: inline-flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 0;
}

:deep(.el-date-editor.el-input__wrapper) {
  width: 100%;
  min-width: 280px;
  flex: 1;
}

@media (max-width: 768px) {
  .quick-date-range-picker {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  
  .el-divider {
    display: none;
  }
  
  .quick-selection {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    flex-wrap: nowrap;
    padding-bottom: 4px;
  }
  
  :deep(.el-date-editor.el-input__wrapper) {
    min-width: 100%;
  }
}

@media (min-width: 769px) and (max-width: 1200px) {
  .quick-date-range-picker {
    flex-wrap: wrap;
  }
  
  .quick-selection {
    flex-wrap: wrap;
  }
  
  :deep(.el-date-editor.el-input__wrapper) {
    min-width: 240px;
  }
}
</style>
